import { Links } from '@/application/constants/links';
import React, { PropsWithChildren, useCallback, useEffect, useRef, useState } from 'react';

import {
  handleAuthenticationSignInCallback,
  handleAuthenticationSignOutCallback,
  SignInRedirectHandler,
  SignOutRedirectHandler,
} from './openid-manager';
import { signInCallbackPath, signOutCallbackPath } from './openid-settings';

export const OpenIdCallback: React.FC<
  PropsWithChildren<{
    signInRedirectHandler: SignInRedirectHandler;
    signOutRedirectHandler: SignOutRedirectHandler;
    loading?: React.ReactNode | undefined;
    error?:
      | ((props: { error: string; error_description: string }) => React.ReactElement | null)
      | undefined;
  }>
> = (props) => {
  const url = window.location.pathname;
  const searchParams = new URLSearchParams(window.location.search);
  const [errorState, setErrorState] = useState<{
    error?: string;
    error_description?: string;
  } | null>(null);
  const error = errorState?.error ?? searchParams.get('error');
  const error_description = errorState?.error_description ?? searchParams.get('error_description');
  const socialLoginDisabledKey = 'SocialLoginDisabled';

  const isAuthCallback = url.startsWith(signInCallbackPath);
  const isSignOutCallback = url.startsWith(signOutCallbackPath);
  const [_, setRerender] = useState(0);
  const rerenderWhenRedirectCompletes = useCallback(() => {
    setTimeout(() => setRerender(new Date().getTime()));
  }, []);

  const isOpenIdHandled = useRef(false);
  useEffect(() => {
    if (isOpenIdHandled.current) return;
    if (isAuthCallback) {
      isOpenIdHandled.current = true;

      handleAuthenticationSignInCallback(
        (user) => {
          props.signInRedirectHandler(user);
          rerenderWhenRedirectCompletes();
        },
        (e: any) => {
          let error_description = e?.error_description;
          if (!error_description && !e.error) {
            error_description = JSON.stringify(e);
          }
          setErrorState({
            error: e?.error ?? 'Authentication error',
            error_description: error_description,
          });
        },
      );
    }
    if (isSignOutCallback) {
      isOpenIdHandled.current = true;
      handleAuthenticationSignOutCallback(() => {
        props.signOutRedirectHandler();
        rerenderWhenRedirectCompletes();
      });
    }
  }, [isAuthCallback, isSignOutCallback]);

  const shouldRedirectToRegistration =
    isAuthCallback &&
    (error === socialLoginDisabledKey || error_description?.includes(socialLoginDisabledKey));

  useEffect(() => {
    if (!shouldRedirectToRegistration) return;
    const searchParams = new URLSearchParams({ socialLoginDisabled: '1' }).toString();
    window.location.assign(`${Links.Unauthorized.UserRegistration}?${searchParams}`);
  }, [shouldRedirectToRegistration]);

  if (shouldRedirectToRegistration) {
    return props.loading ? <>{props.loading}</> : null;
  }

  if (error) {
    return (
      props.error?.({
        error: error,
        error_description: error_description ?? '',
      }) ?? <>{`ERROR: ${error}. ${error_description}`}</>
    );
  }
  if (isAuthCallback || isSignOutCallback) return props.loading ? <>{props.loading}</> : null;

  return <>{props.children}</>;
};
